import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { isAdminApiEnabled } from "../../config/adminApiFeature";
import { useAppSelector } from "../../store/hooks";
import { listEmailAnalysisRequests } from "../../api/services/serviceRequestsApi";

export default function AdminEmailAnalysisPage() {
  const apiOn = isAdminApiEnabled();
  const unreadRequests = useAppSelector((s) => s.dashboard.data?.unread?.requests ?? 0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    if (!apiOn) return;
    setLoading(true);
    setError(null);
    try {
      const { data: payload } = await listEmailAnalysisRequests();
      const items = Array.isArray(payload?.items) ? payload.items : [];
      setRows(items);
    } catch (e) {
      setError(e?.message || "Could not load analysis requests.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [apiOn]);

  useEffect(() => {
    load();
  }, [load]);

  if (!apiOn) {
    return (
      <div className="admin-dash__page">
        <header className="admin-dash__page-head">
          <p className="admin-dash__page-eyebrow">Email analyser</p>
          <h1 className="admin-dash__h1">Reply analysis history</h1>
          <p className="admin-dash__lede">Enable admin API to load client reply analysis history.</p>
        </header>
      </div>
    );
  }

  return (
    <div className="admin-dash__page">
      <header className="admin-dash__page-head">
        <p className="admin-dash__page-eyebrow">Email analyser</p>
        <h1 className="admin-dash__h1">Reply analysis history</h1>
        <p className="admin-dash__lede">
          Request-level data from{" "}
          <code className="admin-dash__code-inline">GET …/email-analysis/requests</code>. Unread
          requests: <strong>{unreadRequests}</strong>.
        </p>
      </header>

      {error ? (
        <div className="admin-dash__error-banner" role="alert">
          {error}{" "}
          <button type="button" className="admin-dash__btn-text" onClick={load}>
            Retry
          </button>
        </div>
      ) : null}

      <div className="admin-dash__table-panel">
        <div className="admin-dash__table-wrap admin-dash__table-wrap--scroll">
          <table className="admin-dash__table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Request ID</th>
                <th>Analysed</th>
                <th>Total inbound</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="admin-dash__empty">
                    Loading analysis requests…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="admin-dash__empty">
                    No email analysis requests available.
                  </td>
                </tr>
              ) : (
                rows.map((it) => (
                  <tr key={it.requestId}>
                    <td>
                      <div className="admin-dash__email-analysis-client">{it.client?.name || "—"}</div>
                      <div className="admin-dash__td-muted">{it.client?.email || "—"}</div>
                      {it.client?.companyName ? (
                        <div className="admin-dash__td-muted">{it.client.companyName}</div>
                      ) : null}
                    </td>
                    <td>
                      <Link
                        className="admin-dash__link"
                        to={`/admin/dashboard/requests/${it.requestId}`}
                      >
                        {it.requestId}
                      </Link>
                    </td>
                    <td>
                      {typeof it.numberOfEmailsAnalysed === "number" ? it.numberOfEmailsAnalysed : 0}
                    </td>
                    <td>{typeof it.totalInboundEmails === "number" ? it.totalInboundEmails : 0}</td>
                    <td>
                      <Link
                        className="admin-dash__table-btn"
                        to={`/admin/dashboard/email-analysis/${it.requestId}`}
                      >
                        Details
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
